import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Combine, ArrowRight, Search, Shield } from "lucide-react";
import { webUsersApi } from "../../lib/api";
import { getApiError } from "../../lib/utils";

/**
 * Modal to merge a Telegram-only shadow user INTO an existing Web user.
 *
 * Direction is one-way: TG identifiers on `source` are moved to `target`
 * and the source doc is deleted. Backend refuses collision (both docs
 * bound to different Telegram IDs).
 *
 * @param {object} props
 * @param {object} props.source  Telegram-only user being absorbed.
 * @param {() => void} props.onClose
 * @param {() => void} props.onMerged   Called after successful merge.
 */
export const MergeUserModal = ({ source, onClose, onMerged }) => {
  const [webUsers, setWebUsers] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    webUsersApi.list()
      .then((r) => { if (alive) setWebUsers(r || []); })
      .catch((e) => { if (alive) setLoadErr(getApiError(e, "Failed to load web users")); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!Array.isArray(webUsers)) return [];
    const needle = q.trim().toLowerCase();
    // Never allow merging INTO the source itself.
    const rows = webUsers.filter((u) => u.user_id !== source.user_id);
    if (!needle) return rows.slice(0, 50);
    return rows.filter((u) =>
      (u.display_name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle),
    ).slice(0, 50);
  }, [webUsers, q, source]);

  const doMerge = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await webUsersApi.merge(target.user_id, source.user_id);
      toast.success(`Merged ${source.display_name} into ${target.display_name}`);
      onMerged();
    } catch (e) {
      toast.error(getApiError(e, "Merge failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-md border border-slate-200 bg-white shadow-2xl"
        data-testid="merge-user-modal"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Combine size={18} className="text-nmi-700" strokeWidth={2.5} />
              <h3 className="font-heading text-lg font-bold text-slate-900">Merge into web user</h3>
            </div>
            <p className="mt-1 text-xs text-slate-600">
              Collapses <b>{source.display_name}</b>'s Telegram binding
              {source.telegram_username && <span className="ml-1 text-slate-400">(@{source.telegram_username})</span>}
              {" "}onto an existing web user so the person has ONE account.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Source summary */}
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">
            Source (will be deleted)
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-slate-900">{source.display_name}</span>
            {source.telegram_id && (
              <span className="rounded-sm bg-white border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                TG {source.telegram_id}
              </span>
            )}
            {source.telegram_username && (
              <span className="text-[11px] text-slate-500">@{source.telegram_username}</span>
            )}
          </div>
        </div>

        {/* Target picker */}
        <div className="px-6 py-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">
            Target (kept, absorbs the source)
          </div>
          <div className="relative mb-3">
            <Search size={12} className="absolute left-2.5 top-2.5 text-slate-400" strokeWidth={2.5} />
            <input
              data-testid="merge-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
            />
          </div>

          {loadErr && (
            <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">{loadErr}</div>
          )}

          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="merge-target-list">
            {webUsers === null && !loadErr && (
              <div className="p-4 text-center text-slate-500"><Loader2 size={16} className="inline animate-spin" /></div>
            )}
            {webUsers && filtered.length === 0 && (
              <div className="p-4 text-center text-xs text-slate-500">
                No matching web users.
              </div>
            )}
            {filtered.map((u) => {
              const isSelected = target?.user_id === u.user_id;
              const hasTg = !!u.telegram_id;
              const collision = hasTg && source.telegram_id && u.telegram_id !== source.telegram_id;
              return (
                <button
                  key={u.user_id}
                  data-testid={`merge-target-${u.user_id}`}
                  onClick={() => setTarget(collision ? null : u)}
                  disabled={collision}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? "bg-nmi-50 border-l-2 border-nmi-700"
                      : collision
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm font-semibold text-slate-900 truncate">{u.display_name}</div>
                      {u.is_owner && <Shield size={11} className="text-amber-600" />}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email || <em>no email</em>}</div>
                    <div className="mt-0.5 text-[10px] font-mono text-slate-400 truncate">{u.user_id}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {hasTg ? (
                      <div className={`text-[10px] font-mono ${collision ? "text-red-600" : "text-slate-500"}`}>
                        TG {u.telegram_id}
                        {collision && <div className="text-[9px] font-semibold uppercase mt-0.5">conflict</div>}
                      </div>
                    ) : (
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">no TG</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {target && (
          <div className="mx-6 mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="flex items-center gap-2 font-semibold">
              <ArrowRight size={12} strokeWidth={2.5} />
              After merge
            </div>
            <ul className="mt-1.5 space-y-0.5 list-disc pl-4">
              <li><b>{source.display_name}</b>'s Telegram binding moves onto <b>{target.display_name}</b>.</li>
              <li>The <b>{source.display_name}</b> record is deleted permanently.</li>
              <li>Audit log preserves the merge with actor + timestamp.</li>
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="merge-submit"
            onClick={doMerge}
            disabled={!target || busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Combine size={12} strokeWidth={2.5} />}
            Merge into target
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeUserModal;
